import { useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { auth } from '../../../utils/auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Custom hook for email actions (reply, mark read, assign to case, etc.)
 */
export const useEmailActions = ({ onRefresh }) => {
  // Reply state
  const [isReplying, setIsReplying] = useState(false);
  const [replyMode, setReplyMode] = useState('reply');
  const [replyBody, setReplyBody] = useState('');
  const [replyTone, setReplyTone] = useState('professional');
  const [sendingReply, setSendingReply] = useState(false);
  const [generatingAI, setGeneratingAI] = useState(false);
  
  // Assignment state
  const [isAssigning, setIsAssigning] = useState(false);
  
  // Mark email as read
  const markAsRead = useCallback(async (emailId) => {
    try {
      const token = auth.getToken();
      await axios.put(
        `${API}/inbox/messages/${emailId}/read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (err) {
      console.error('Error marking as read:', err);
    }
  }, []);
  
  // Assign email to case
  const assignToCase = useCallback(async (emailId, caseId) => {
    setIsAssigning(true);
    try {
      const token = auth.getToken();
      await axios.post(
        `${API}/inbox/triage/email/${emailId}/assign-case`,
        { case_id: caseId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('E-post tilknyttet sak');
      onRefresh?.();
    } catch (err) {
      toast.error('Kunne ikke tilknytte sak');
    } finally {
      setIsAssigning(false);
    }
  }, [onRefresh]);
  
  // Start reply
  const startReply = useCallback((mode = 'reply') => {
    setReplyMode(mode);
    setReplyBody('');
    setIsReplying(true);
  }, []);
  
  // Cancel reply
  const cancelReply = useCallback(() => {
    setIsReplying(false);
    setReplyBody('');
  }, []);
  
  // Generate AI reply - WORLD CLASS with user's actual signature
  const generateAIReply = useCallback(async (email) => {
    if (!email) return;
    
    setGeneratingAI(true);
    try {
      const token = auth.getToken();
      const response = await axios.post(
        `${API}/inbox/triage/email/${email.id}/quick-reply`,
        { tone: replyTone },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const draft = response.data.draft || '';
      const userSignatureFromSettings = response.data.user_signature || null;
      
      // Build signature - prefer user's saved signature, otherwise generate one
      let signature = '';
      
      if (userSignatureFromSettings) {
        // Use user's actual signature from settings
        signature = `<div style="margin-top: 24px; padding-top: 16px; border-top: 1px solid #e5e7eb;">${userSignatureFromSettings}</div>`;
      } else {
        // Generate professional signature from user info
        try {
          const userResponse = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const user = userResponse.data;
          if (user) {
            const name = user.name || user.email?.split('@')[0] || '';
            const title = user.title || 'Advokat';
            const firm = user.firm || user.tenant_name || '';
            const phone = user.phone || '';
            const address = user.address || '';
            const website = user.website || '';
            
            // Clean, professional signature - matching the user's style
            const userEmail = user.email || '';
            signature = `
<div style="margin-top: 24px; padding-top: 16px; border-top: 1px solid #e5e7eb; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif;">
  <p style="margin: 0 0 2px 0; font-size: 14px; font-weight: 600; color: #111827;">${name}</p>
  <p style="margin: 0 0 12px 0; font-size: 13px; color: #6b7280;">${title}</p>
  ${firm ? `<p style="margin: 0 0 4px 0; font-size: 13px; font-weight: 600; color: #374151; text-transform: uppercase; letter-spacing: 0.5px;">${firm}</p>` : ''}
  <p style="margin: 0 0 2px 0; font-size: 13px; color: #6b7280;"><a href="mailto:${userEmail}" style="color: #6b7280; text-decoration: none;">${userEmail}</a></p>
  ${phone ? `<p style="margin: 0; font-size: 13px; color: #6b7280;">${phone}</p>` : ''}
</div>`;
          }
        } catch (e) {
          // Minimal signature fallback
          signature = '';
        }
      }
      
      // Convert plain text to beautiful HTML formatting - WORLD CLASS
      const formatAsHtml = (text: string) => {
        // First, convert **bold** to <strong>
        let processedText = text.replace(/\*\*([^*]+)\*\*/g, '<strong style="font-weight: 600;">$1</strong>');
        
        // Split into paragraphs
        const paragraphs = processedText.split(/\n\n+/);
        
        return paragraphs.map((para, idx) => {
          // Check for greeting (first paragraph, typically short)
          if (idx === 0 && para.length < 100 && !para.includes('\n')) {
            return `<p style="margin-bottom: 16px; font-size: 15px;">${para}</p>`;
          }
          
          // Check for bullet points (• or - or *)
          if (para.includes('\n- ') || para.includes('\n• ') || para.includes('\n* ') || 
              para.startsWith('- ') || para.startsWith('• ') || para.startsWith('* ')) {
            const lines = para.split('\n');
            const items = lines
              .filter(line => /^[\-\•\*]\s/.test(line.trim()))
              .map(line => `<li style="margin-bottom: 8px; padding-left: 4px;">${line.replace(/^[\-\•\*]\s*/, '')}</li>`)
              .join('');
            if (items) {
              return `<ul style="margin: 16px 0; padding-left: 24px; list-style-type: disc;">${items}</ul>`;
            }
          }
          
          // Check for numbered list
          if (/^\d+[\.\)]\s/.test(para)) {
            const lines = para.split('\n');
            const items = lines
              .filter(line => /^\d+[\.\)]\s/.test(line.trim()))
              .map(line => `<li style="margin-bottom: 8px; padding-left: 4px;">${line.replace(/^\d+[\.\)]\s*/, '')}</li>`)
              .join('');
            if (items) {
              return `<ol style="margin: 16px 0; padding-left: 24px;">${items}</ol>`;
            }
          }
          
          // Check for signature line (Best regards, etc.) - add extra spacing
          if (/^(Best regards|Med vennlig hilsen|Vennlig hilsen|Beste hilsen|Kind regards|Regards|Sincerely)/i.test(para.trim())) {
            return `<p style="margin-top: 32px; margin-bottom: 4px;">${para.trim()}</p>`;
          }
          
          // Regular paragraph - preserve line breaks
          return `<p style="margin-bottom: 12px; line-height: 1.7;">${para.replace(/\n/g, '<br>')}</p>`;
        }).filter(Boolean).join('');
      };
      
      // Add line break before signature
      const htmlContent = formatAsHtml(draft) + '<br>' + signature;
      setReplyBody(htmlContent);
    } catch (err) {
      toast.error('Kunne ikke generere AI-svar');
    } finally {
      setGeneratingAI(false);
    }
  }, [replyTone]);
  
  // Send reply - Now sends HTML
  const sendReply = useCallback(async (email) => {
    if (!email || !replyBody.trim()) return;
    
    setSendingReply(true);
    try {
      const token = auth.getToken();
      const messageId = email.outlook_message_id || email.id;
      
      // Strip HTML tags to check if there's actual content
      const textContent = replyBody.replace(/<[^>]*>/g, '').trim();
      if (!textContent) {
        toast.error('Skriv et svar før du sender');
        setSendingReply(false);
        return;
      }
      
      await axios.post(
        `${API}/outlook/reply`,
        {
          message_id: messageId,
          body: replyBody,
          is_html: true,
          reply_all: replyMode === 'reply-all'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Svar sendt!');
      setIsReplying(false);
      setReplyBody('');
      onRefresh?.();
    } catch (err) {
      toast.error('Kunne ikke sende svar');
    } finally {
      setSendingReply(false);
    }
  }, [replyBody, replyMode, onRefresh]);
  
  return {
    // Reply state
    isReplying,
    replyMode,
    replyBody,
    replyTone,
    sendingReply,
    generatingAI,
    
    // Reply actions
    startReply,
    cancelReply,
    setReplyBody,
    setReplyTone,
    generateAIReply,
    sendReply,
    
    // Other actions
    markAsRead,
    assignToCase,
    isAssigning
  };
};

export default useEmailActions;
